import { Button, Card, Header } from "@rothoblaas/design-system";
import { FC, useEffect } from "react";
import { useAppContext } from "../context/appContext";
import { useAuthContext } from "../context/authContext";
import useI18n from "../hooks/useTranslations";
import styles from "./AccessDeniedPage.module.css";

const RECHECK_INTERVAL_MS = 5000;

/**
 * Full-page replacement for an app the current user may not use: the backend's access check refused
 * this app's scope, because the user is blocked, the app is locked and they're not on the allow list,
 * or the app is geo-blocked. The same plain "forbidden" in every case: users are never told why.
 * Shared by the main app and the single-module apps, which only differ by Logo.
 */
const AccessDeniedPage: FC = () => {
  const i18n = useI18n();
  const { Logo } = useAppContext();
  const { identity, logout, recheckAccess } = useAuthContext();

  const t = i18n.translation.accessControl;

  // The rules may change while they wait (unblocked, app unlocked, allow-listed, geo-unblocked):
  // re-check in the background, and the gate swaps this page for the app as soon as access is
  // granted. Each check is scheduled after the previous one has finished, so slow responses never
  // pile up.
  useEffect(() => {
    let cancelled = false;
    let timeout: ReturnType<typeof setTimeout>;
    const schedule = () => {
      timeout = setTimeout(() => {
        recheckAccess().finally(() => {
          if (!cancelled) schedule();
        });
      }, RECHECK_INTERVAL_MS);
    };
    schedule();
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, []);

  return (
    <div className={styles.page}>
      <Header
        locale={i18n.translation.languageCode}
        isStaging={import.meta.env.MODE === "staging"}
        title={
          <div className={styles.logo}>
            <Logo />
          </div>
        }
      />
      <main className={styles.content}>
        <Card className={styles.card} title={t.forbiddenTitle}>
          <p className={styles.text}>{t.forbiddenText}</p>
          {identity && (
            <p className={styles.identity}>
              {t.signedInAs} <strong>{identity.email}</strong>
            </p>
          )}
          <div className={styles.actions}>
            <Button onClick={logout}>{i18n.translation.common.logout}</Button>
          </div>
        </Card>
      </main>
    </div>
  );
};

export default AccessDeniedPage;
